/**
 * The community help channel, from the browser's side.
 *
 * Everything goes through our own origin: the gateway holds the observability
 * secret and forwards, so nothing sensitive ever reaches the page. What the
 * page does hold is the **read token** — minted when somebody first writes,
 * kept in their browser, and required to read the conversation back. An
 * address is public; a support history is not, and without a token knowing
 * somebody's wallet would be enough to read what they told us.
 *
 * The cost of that choice, stated plainly: clearing site data or moving to
 * another browser loses the link to an existing conversation, and the next
 * message opens a fresh view of it. The thread itself is never lost — it is
 * keyed by wallet on our side — so a moderator can always reunite the two.
 */

export type SupportAuthor = 'user' | 'moderator'

export interface SupportAttachmentMeta {
  name: string
  mime: string
  size: number
}

export interface SupportMessage {
  author: SupportAuthor
  text: string
  ts: string
  attachments: SupportAttachmentMeta[]
}

export interface SupportThread {
  status: 'new' | 'answered' | 'resolved'
  messages: SupportMessage[]
}

export interface OutgoingAttachment {
  name: string
  mime: string
  dataBase64: string
}

const tokenKey = (wallet: string) => `mprolab.support.token.${wallet.toLowerCase()}`

export const readStoredToken = (wallet?: string): string | null => {
  if (!wallet) return null
  try {
    return window.localStorage.getItem(tokenKey(wallet))
  } catch {
    // Private windows and blocked storage: the widget still sends, it just
    // cannot show history.
    return null
  }
}

const storeToken = (wallet: string, token: string): void => {
  try {
    window.localStorage.setItem(tokenKey(wallet), token)
  } catch {
    /* nothing to do — see above */
  }
}

/** Send one message. Returns the read token to come back with. */
export async function sendSupportMessage(params: {
  wallet: string
  message: string
  attachments: OutgoingAttachment[]
}): Promise<{ accepted: boolean; readToken?: string }> {
  const res = await fetch('/api/support/message', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      wallet: params.wallet,
      message: params.message,
      attachments: params.attachments,
      href: window.location.href
    })
  })
  if (!res.ok) return { accepted: false }
  const body = (await res.json().catch(() => ({}))) as {
    accepted?: boolean
    readToken?: string
  }
  if (body.readToken) storeToken(params.wallet, body.readToken)
  return { accepted: body.accepted === true, readToken: body.readToken }
}

/** The conversation this browser is allowed to read. */
export async function fetchSupportThread(
  token: string
): Promise<SupportThread | null> {
  try {
    const res = await fetch(
      `/api/support/thread?token=${encodeURIComponent(token)}`
    )
    if (!res.ok) return null
    const body = (await res.json()) as SupportThread & { found?: boolean }
    if (body.found === false || !Array.isArray(body.messages)) return null
    return { status: body.status, messages: body.messages }
  } catch {
    return null
  }
}

/** What a browser may attach, mirroring the limits the service enforces. */
export const MAX_ATTACHMENTS = 3
export const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024
export const ACCEPTED_MIME = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
  'text/plain'
]

/** Read a picked file into what the endpoint expects, or null if it is out of
 *  bounds — the caller says why, rather than failing silently at the server. */
export function readAttachment(file: File): Promise<OutgoingAttachment | null> {
  if (!ACCEPTED_MIME.includes(file.type)) return Promise.resolve(null)
  if (file.size > MAX_ATTACHMENT_BYTES) return Promise.resolve(null)
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onerror = () => resolve(null)
    reader.onload = () => {
      const result = String(reader.result ?? '')
      // "data:image/png;base64,AAAA" — the endpoint stores the payload only.
      const comma = result.indexOf(',')
      resolve(
        comma === -1
          ? null
          : { name: file.name, mime: file.type, dataBase64: result.slice(comma + 1) }
      )
    }
    reader.readAsDataURL(file)
  })
}
